import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, Bell, Percent, Coins } from "lucide-react";
import api, { formatApiError } from "../api/client";
import { AdminTabs } from "./AdminDashboard";

export default function AdminConfig() {
    const [cfg, setCfg] = useState(null);
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const [fees, setFees] = useState(null);   // {entry, settlement, defaults, duration_keys}
    const [feesDraft, setFeesDraft] = useState(null);
    const [feeBusy, setFeeBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/config");
            setCfg(r.data);
            setDraft(r.data);
        } catch {}
        try {
            const f = await api.get("/admin/fees");
            setFees(f.data);
            setFeesDraft({
                entry: { ...f.data.entry },
                settlement: { ...f.data.settlement },
            });
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            const patch = {};
            for (const k of Object.keys(draft)) {
                if (draft[k] !== cfg[k]) patch[k] = parseFloat(draft[k]);
            }
            if (Object.keys(patch).length === 0) {
                toast.message("No changes to save");
                setBusy(false);
                return;
            }
            const r = await api.patch("/admin/config", patch);
            setCfg(r.data); setDraft(r.data);
            toast.success("Configuration saved", { description: `Updated ${Object.keys(patch).length} field(s)` });
        } catch (e) {
            toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setBusy(false);
        }
    };

    const set = (k, v) => setDraft({ ...draft, [k]: v });

    const setFee = (bucket, key, raw) => {
        const v = raw === "" ? "" : parseFloat(raw);
        setFeesDraft({
            ...feesDraft,
            [bucket]: { ...feesDraft[bucket], [key]: v },
        });
    };

    const saveFees = async () => {
        setFeeBusy(true);
        try {
            const patch = { entry: {}, settlement: {} };
            for (const bucket of ["entry", "settlement"]) {
                for (const k of Object.keys(feesDraft[bucket])) {
                    const next = feesDraft[bucket][k];
                    if (next === "" || Number.isNaN(next)) continue;
                    if (Math.abs(next - fees[bucket][k]) > 1e-12) patch[bucket][k] = next;
                }
            }
            const hasChanges = Object.keys(patch.entry).length || Object.keys(patch.settlement).length;
            if (!hasChanges) { toast.message("No fee changes to save"); setFeeBusy(false); return; }
            const r = await api.patch("/admin/fees", patch);
            setFees({ ...fees, entry: r.data.entry, settlement: r.data.settlement });
            setFeesDraft({ entry: { ...r.data.entry }, settlement: { ...r.data.settlement } });
            const count = Object.keys(patch.entry).length + Object.keys(patch.settlement).length;
            toast.success("Fee schedule updated", { description: `Saved ${count} per-duration rate(s) — applied immediately to new trades.` });
        } catch (e) {
            toast.error("Fee save failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setFeeBusy(false);
        }
    };

    const resetFees = async () => {
        if (!window.confirm("Reset all fee tiers to defaults?")) return;
        setFeeBusy(true);
        try {
            const r = await api.post("/admin/fees/reset");
            setFees({ ...fees, entry: r.data.entry, settlement: r.data.settlement });
            setFeesDraft({ entry: { ...r.data.entry }, settlement: { ...r.data.settlement } });
            toast.success("Fee schedule reset to defaults");
        } catch (e) {
            toast.error("Reset failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setFeeBusy(false);
        }
    };

    if (!cfg) return <div className="min-h-[60vh] grid place-items-center font-mono text-xs text-zinc-500">Loading…</div>;

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-10 grid gap-6">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Configuration</div>
                    <h1 className="text-3xl font-medium tracking-tighter mt-2">Configurations</h1>
                </div>
                <AdminTabs />

                <div className="grid lg:grid-cols-2 gap-3">
                    <Card icon={Bell} title="Notification Thresholds" subtitle="Margin-depletion levels that trigger trader alerts.">
                        <FieldNum label="1st (info trigger)" suffix="%" value={draft.notification_pct_1} onChange={(v) => set("notification_pct_1", v)} hint="Default 100 · banner shown when margin used ≥ this %." testid="cfg-notify-1" />
                        <FieldNum label="2nd (warning trigger)" suffix="%" value={draft.notification_pct_2} onChange={(v) => set("notification_pct_2", v)} hint="Default 80 · stronger warning shown when margin used ≥ this %." testid="cfg-notify-2" />
                    </Card>

                    <Card icon={Percent} title="Bet Participate" subtitle="Minimum and maximum margin as % of notional.">
                        <FieldNum label="Min Limit" suffix="%" value={draft.min_participation_pct} onChange={(v) => set("min_participation_pct", v)} hint="Default 1.5 · floor for margin (≥1% always enforced)." testid="cfg-min" />
                        <FieldNum label="Max Limit" suffix="%" value={draft.max_participation_pct} onChange={(v) => set("max_participation_pct", v)} hint="Default 100 · ceiling (100% = 1× leverage, max collateralised)." testid="cfg-max" />
                    </Card>

                    <Card icon={AlertOctagon} title="Liquidation" subtitle="Auto-liquidate active swaps at this depletion level." accent="rose">
                        <FieldNum label="Liquidation Threshold" suffix="%" value={draft.liquidation_threshold_pct} onChange={(v) => set("liquidation_threshold_pct", v)} hint="Default 95 · position auto-closes when this much of margin is consumed." testid="cfg-liq-threshold" />
                        <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm px-3 py-2 mt-2 flex items-start gap-2">
                            <AlertOctagon className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
                            <div className="text-[10px] font-mono leading-relaxed text-rose-200">
                                Currently set to <span className="font-semibold">{cfg.liquidation_threshold_pct}%</span> — settlement loop scans every 2s and closes any side once its margin threshold drops to <span className="font-semibold">{(100 - cfg.liquidation_threshold_pct).toFixed(0)}%</span> remaining.
                            </div>
                        </div>
                    </Card>

                    <Card icon={Coins} title="Fee Structure (read-only)" subtitle="Liquidation & early-exit. Per-duration entry / settlement fees are edited in the table below.">
                        <div className="grid gap-1.5 text-xs font-mono">
                            <Row label="Liquidation loss (loser → winner)" value="95% of loser margin" />
                            <Row label="Liquidation fee (leftover sweep)" value="up to 5% of margin, capped at 2% notional" color="text-rose-300" />
                            <Row label="Early-exit penalty" value="2% margin → counterparty · 1% margin → protocol" color="text-amber-300" />
                            <Row label="Service fee policy" value="both winner and loser pay each per-side rate" color="text-cyan-300" />
                        </div>
                    </Card>
                </div>

                {/* ---- Per-duration fee schedule (editable, persisted in DB) ---- */}
                {fees && feesDraft && (
                    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-6" data-testid="admin-fee-schedule">
                        <div className="flex items-center gap-2.5">
                            <Coins className="w-4 h-4 text-cyan-400" />
                            <div className="text-sm font-medium tracking-tight">Per-duration fee schedule</div>
                        </div>
                        <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">
                            Entry fee = charged per side at match. Settlement fee = charged per side at expiry. Both winner and loser pay each per-side rate. Changes apply to all new trades immediately.
                        </p>

                        <div className="mt-5 overflow-x-auto">
                            <table className="w-full text-xs font-mono">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-[#1F1F24]">
                                        <th className="text-left py-2 pr-3">Duration</th>
                                        <th className="text-right py-2 px-3">Entry fee % (per side)</th>
                                        <th className="text-right py-2 px-3">Settlement fee % (per side)</th>
                                        <th className="text-right py-2 pl-3">Entry default</th>
                                        <th className="text-right py-2 pl-3">Settle default</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(fees.duration_keys || Object.keys(fees.entry)).map((k) => {
                                        const ePct = feesDraft.entry[k];
                                        const sPct = feesDraft.settlement[k];
                                        const eDef = fees.defaults?.entry?.[k] ?? 0;
                                        const sDef = fees.defaults?.settlement?.[k] ?? 0;
                                        const dirty = Math.abs((ePct || 0) - fees.entry[k]) > 1e-12
                                                   || Math.abs((sPct || 0) - fees.settlement[k]) > 1e-12;
                                        return (
                                            <tr key={k} className={`border-b border-[#1F1F24]/60 ${dirty ? "bg-cyan-500/5" : ""}`}>
                                                <td className="py-2 pr-3 text-zinc-100 uppercase">{k}</td>
                                                <td className="py-2 px-3 text-right">
                                                    <input
                                                        data-testid={`fee-entry-${k}`}
                                                        type="number" step="any" min="0"
                                                        value={ePct === "" ? "" : (ePct * 100).toFixed(4)}
                                                        onChange={(e) => setFee("entry", k, e.target.value === "" ? "" : (parseFloat(e.target.value) / 100))}
                                                        className="w-24 bg-transparent text-right text-cyan-300 outline-none border-b border-[#1F1F24] focus:border-cyan-400 py-1 tabular-nums"
                                                    />
                                                </td>
                                                <td className="py-2 px-3 text-right">
                                                    <input
                                                        data-testid={`fee-settle-${k}`}
                                                        type="number" step="any" min="0"
                                                        value={sPct === "" ? "" : (sPct * 100).toFixed(4)}
                                                        onChange={(e) => setFee("settlement", k, e.target.value === "" ? "" : (parseFloat(e.target.value) / 100))}
                                                        className="w-24 bg-transparent text-right text-cyan-300 outline-none border-b border-[#1F1F24] focus:border-cyan-400 py-1 tabular-nums"
                                                    />
                                                </td>
                                                <td className="py-2 pl-3 text-right text-zinc-600 tabular-nums">{(eDef * 100).toFixed(3)}%</td>
                                                <td className="py-2 pl-3 text-right text-zinc-600 tabular-nums">{(sDef * 100).toFixed(3)}%</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                            <div className="text-[10px] font-mono text-zinc-500">
                                Values are entered as <span className="text-zinc-300">percent</span> (e.g. <span className="text-cyan-300">0.025</span> for 0.025%). Each side pays the full per-side rate — total protocol take per swap is <span className="text-zinc-300">2 × rate</span>.
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid="fee-reset-button"
                                    onClick={resetFees}
                                    disabled={feeBusy}
                                    className="px-4 py-2 border border-[#1F1F24] hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40"
                                >
                                    Reset to defaults
                                </button>
                                <button
                                    data-testid="fee-save-button"
                                    onClick={saveFees}
                                    disabled={feeBusy}
                                    className="px-6 py-2 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 text-black text-xs uppercase tracking-widest font-semibold rounded-sm"
                                >
                                    {feeBusy ? "…" : "Save fee schedule"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between bg-[#0A0A0C] border border-[#1F1F24] rounded-sm px-5 py-4">
                    <div className="text-[10px] font-mono text-zinc-500">Changes apply protocol-wide immediately after Save.</div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setDraft(cfg)}
                            className="px-5 py-2 border border-[#1F1F24] hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-widest font-semibold rounded-sm"
                        >
                            Discard
                        </button>
                        <button
                            data-testid="config-save-button"
                            onClick={save}
                            disabled={busy}
                            className="px-6 py-2 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 text-black text-xs uppercase tracking-widest font-semibold rounded-sm"
                        >
                            {busy ? "…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, color = "text-zinc-100" }) {
    return (
        <div className="flex justify-between text-zinc-300">
            <span className="text-zinc-500">{label}</span>
            <span className={color}>{value}</span>
        </div>
    );
}

function Card({ icon: Icon, title, subtitle, children, accent }) {
    const borderClass = accent === "rose" ? "border-rose-500/20" : "border-[#1F1F24]";
    return (
        <div className={`bg-[#0A0A0C] border ${borderClass} rounded-sm p-6`}>
            <div className="flex items-center gap-2.5">
                <Icon className={`w-4 h-4 ${accent === "rose" ? "text-rose-400" : "text-cyan-400"}`} />
                <div className="text-sm font-medium tracking-tight">{title}</div>
            </div>
            <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">{subtitle}</p>
            <div className="mt-5 grid gap-4">{children}</div>
        </div>
    );
}

function FieldNum({ label, suffix, value, onChange, hint, testid }) {
    return (
        <div className="grid gap-1.5">
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</label>
            <div className="flex items-center gap-2 border-b border-[#1F1F24] focus-within:border-cyan-400 transition-colors">
                <input
                    data-testid={testid}
                    type="number"
                    step="any"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="bg-transparent outline-none py-1.5 font-mono text-base flex-1 min-w-0"
                />
                <span className="text-xs font-mono text-zinc-500">{suffix}</span>
            </div>
            {hint && <div className="text-[10px] font-mono text-zinc-600 leading-relaxed">{hint}</div>}
        </div>
    );
}
